import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { environment } from "../../../../environments/environment";

/**
 * Base Service for 4Bridge API calls
 * Provides common functionality for all Bridge services
 */
export abstract class BridgeBaseService<T> {
    protected readonly baseUrl: string;

    constructor(
        protected httpClient: HttpClient,
        protected endpoint: string
    ) {
        this.baseUrl = `${environment.api.API_ENDPOINT}/4bridge`;
    }

    /**
     * Get all items
     */
    public getAll(): Observable<T[]> {
        return this.httpClient
            .get<T[]>(`${this.baseUrl}/${this.endpoint}`)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    /**
     * Get item by ID
     */
    public getById(id: string): Observable<T> {
        return this.httpClient
            .get<T>(`${this.baseUrl}/${this.endpoint}/${id}`)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    /**
     * Create new item
     */
    public create(item: Partial<T>): Observable<T> {
        return this.httpClient
            .post<T>(`${this.baseUrl}/${this.endpoint}`, item)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    /**
     * Update existing item
     */
    public update(id: string, item: Partial<T>): Observable<T> {
        return this.httpClient
            .put<T>(`${this.baseUrl}/${this.endpoint}/${id}`, item)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    /**
     * Delete item
     */
    public delete(id: string): Observable<any> {
        return this.httpClient
            .delete(`${this.baseUrl}/${this.endpoint}/${id}`)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    /**
     * Error handler
     */
    protected handleError(error: HttpErrorResponse): Observable<never> {
        let errorMessage = 'An error occurred';

        if (error.error instanceof ErrorEvent) {
            // Client-side error
            errorMessage = error.error.message;
        } else {
            // Server-side error
            errorMessage = error.error?.message || error.message || 'Server error';
        }

        console.error('Bridge API Error:', errorMessage, error);
        return throwError(() => ({ message: errorMessage, status: error.status, error: error.error }));
    }
}
